import { useEffect, useMemo, useRef, useState } from "react";

import type { ReactNode } from "react";

interface PcbCardProps {
  children: ReactNode;
  label?: string;
  labelPosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  /** Second label rendered at bottom-left (e.g. category like "AGENTS") */
  categoryLabel?: string;
  title?: string;
  headerRight?: ReactNode;
  className?: string;
  /** Chamfer corner cut in px (default 12) */
  cut?: number;
}

function buildSvg(w: number, h: number, cut: number) {
  if (w < 1 || h < 1) return null;

  // Octagon vertices (clockwise from top-left chamfer)
  const verts = [
    [cut, 0],
    [w - cut, 0],
    [w, cut],
    [w, h - cut],
    [w - cut, h],
    [cut, h],
    [0, h - cut],
    [0, cut],
  ] as const;

  const outerPath =
    verts
      .map((v, i) => `${i === 0 ? "M" : "L"}${String(v[0])},${String(v[1])}`)
      .join(" ") + " Z";

  // Inner edge (4px inset — matching landing page)
  const inset = 4;
  const innerVerts = [
    [cut + inset, inset],
    [w - cut - inset, inset],
    [w - inset, cut + inset],
    [w - inset, h - cut - inset],
    [w - cut - inset, h - inset],
    [cut + inset, h - inset],
    [inset, h - cut - inset],
    [inset, cut + inset],
  ] as const;

  const innerPath =
    innerVerts
      .map((v, i) => `${i === 0 ? "M" : "L"}${String(v[0])},${String(v[1])}`)
      .join(" ") + " Z";

  return {
    outerPath,
    innerPath,
    w,
    h,
  };
}

export function PcbCard({
  children,
  label,
  labelPosition = "top-left",
  categoryLabel,
  title,
  headerRight,
  className = "",
  cut = 12,
}: PcbCardProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    // Guard for test environments without ResizeObserver
    if (typeof ResizeObserver === "undefined") {
      setDims({ w: el.offsetWidth, h: el.offsetHeight });
      return;
    }

    const update = () => {
      setDims((prev) => {
        const w = el.offsetWidth;
        const h = el.offsetHeight;
        if (Math.abs(prev.w - w) < 1 && Math.abs(prev.h - h) < 1) return prev;
        return { w, h };
      });
    };

    // Use ResizeObserver to trigger, but read offsetWidth/offsetHeight
    // (border-box) so the SVG covers the full card including padding
    const ro = new ResizeObserver(() => {
      update();
    });
    ro.observe(el);
    // Initial measurement
    update();
    return () => {
      ro.disconnect();
    };
  }, []);

  const svg = useMemo(
    () => buildSvg(dims.w, dims.h, cut),
    [dims.w, dims.h, cut],
  );

  const labelClasses: Record<string, string> = {
    "top-left": "top-1 left-3",
    "top-right": "top-1 right-3",
    "bottom-left": "bottom-1 left-3",
    "bottom-right": "bottom-1 right-3",
  };

  const pad = 0;

  return (
    <div
      className={`pcb-card ${className}`}
      style={{ "--cut": `${String(cut)}px` } as React.CSSProperties}
    >
      <div ref={innerRef} className="pcb-card-inner">
        {title && (
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="font-serif text-base font-light text-[var(--color-text)]">
              {title}
            </h3>
            {headerRight && (
              <div className="flex items-center gap-2">{headerRight}</div>
            )}
          </div>
        )}
        {children}
      </div>

      {/* SVG border overlay */}
      {svg && (
        <svg
          className="pcb-border"
          viewBox={`${String(-pad)} ${String(-pad)} ${String(svg.w + pad * 2)} ${String(svg.h + pad * 2)}`}
          width={svg.w + pad * 2}
          height={svg.h + pad * 2}
          style={{ left: -pad, top: -pad }}
        >
          <path className="edge" d={svg.outerPath} fill="none" />
          <path className="inner-edge" d={svg.innerPath} fill="none" />
        </svg>
      )}

      {/* PCB label (primary — configurable position) */}
      {label && (
        <span
          className={`pcb-label ${labelClasses[labelPosition] ?? labelClasses["top-left"] ?? ""}`}
        >
          {label}
        </span>
      )}

      {/* PCB category label (always bottom-left) */}
      {categoryLabel && (
        <span className="pcb-label bottom-1 left-3">{categoryLabel}</span>
      )}
    </div>
  );
}
